import React, { useState } from 'react';
import { Pressable, StyleSheet, ActivityIndicator, Platform, ViewStyle, TextStyle, StyleProp } from 'react-native';
import { Text } from '@/components/common/text';
import { APP_CONSTANTS } from '@/constants/residents';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { onColor } from '@/constants/shared/theme/onColor';

type Props = {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  // Allow hover handlers to be passed through when needed (web)
  onHoverIn?: () => void;
  onHoverOut?: () => void;
};

export const PrimaryButton: React.FC<Props> = ({ title, onPress, disabled, loading, style, textStyle, onHoverIn, onHoverOut }) => {
  const styles = useThemedStyles(makeStyles);
  const { semantic } = useThemeColors();
  const [isHovered, setIsHovered] = useState(false);
  const hoverStyle = Platform.OS === 'web' && isHovered && !disabled ? styles.hover : null;

  return (
    <Pressable
      style={[styles.button, hoverStyle, style as any, disabled && styles.disabled]}
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => { setIsHovered(true); onHoverIn?.(); }}
      onHoverOut={() => { setIsHovered(false); onHoverOut?.(); }}
    >
      {loading ? (
        <ActivityIndicator color={onColor(semantic.interactive.primary)} />
      ) : (
        <Text style={[styles.text, textStyle as any]} numberOfLines={2}>{title}</Text>
      )}
    </Pressable>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  button: {
    minHeight: APP_CONSTANTS.TEXT_FIELD_HEIGHT,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: semantic.interactive.primary,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    ...Platform.select({ web: { cursor: 'pointer' as any } }),
  },
  text: {
    // Derived from the fill so the two cannot drift. Resolves to white today at
    // 4.53:1, which clears AA — it did not before the brand blue was darkened.
    color: onColor(semantic.interactive.primary),
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  disabled: {
    // Deliberately NOT semantic.interactive.disabled. That token is a pale grey
    // in the light theme and would put this button's white label at roughly
    // 1.9:1 — unreadable. This fixed mid grey keeps it near 4.8:1 in both
    // themes. Measured, not assumed; revisit only with a real disabled-fill
    // token that is dark enough to carry white.
    backgroundColor: '#6B7280',
    opacity: 0.6,
  },
  hover: {
    opacity: 0.85,
  },
});


